import { inject, Injectable, InjectionToken, Optional } from '@angular/core';
import { EMPTY, from, Observable, of } from 'rxjs';
import { catchError, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { v4 as uuidv4 } from 'uuid';

import { FormSignatureService, LocalizedName } from '@protocols/domain';

import { AssignedMethodTypesState } from '../state/assigned-method-types.state';
import { LabelConfigState, StagedLabelConfig } from '../state/label-config.state';
import {
  PendingPersistence,
  PipelineCategoryInitialInput,
  PipelineCategoryOption,
  PipelineCategoryResult,
  PipelineCategoryState,
} from '../state/pipeline-category.state';
import { StagedMethodTypesState } from '../state/staged-method-types.state';

// Type-only: IListMethodTypeLabelColorsQuery is a plain interface. Use inject()
// (not constructor injection) so Angular's ctorParameters never references the
// name — the interface has no ESM runtime export and would throw a ReferenceError.
import type { IListMethodTypeLabelColorsQuery } from '@protocols/use-cases';
import type { MethodTypeSelectorItem } from '../component/method-types/method-type-selector/method-type-selector.component';
import type { LabelConfigContextMethodType } from '../state/label-config-multi-assign.state';
import type { MethodTypeItem } from '../state/method-type-list.state';
import type { StagedMethodTypeDraft, StagedMethodTypeOverride } from '../state/staged-method-types.state';

export const LIST_METHOD_TYPE_LABEL_COLORS_QUERY = new InjectionToken<IListMethodTypeLabelColorsQuery>(
  'LIST_METHOD_TYPE_LABEL_COLORS_QUERY'
);

/**
 * One snapshot of what this editing session can tell the multi-assign colour
 * dialog. Emitted as a single value so the id can never name a method type
 * missing from the list it travelled with.
 */
export interface LabelConfigDialogContext {
  /** Method types the backend usage query cannot return (new or renamed here). */
  methodTypes: LabelConfigContextMethodType[];
  /** The method type being edited, when the session has one in context. */
  contextMethodTypeId?: string;
}

@Injectable()
export class PipelineCategoryEffects {
  // inject() avoids referencing the interface name in ctorParameters — the
  // interface has no ESM runtime export, so constructor injection via @Inject()
  // would throw a ReferenceError when Angular's JIT DI reflects the parameter.
  //
  // REQUIRED, deliberately: a scope that provides this class without the query
  // must fail at injection rather than render a colour preview that is silently
  // and permanently empty. `inject()` is about HOW the dependency arrives, not
  // whether it is optional.
  private readonly listLabelColorsQuery = inject(LIST_METHOD_TYPE_LABEL_COLORS_QUERY);

  constructor(
    private readonly state: PipelineCategoryState,
    @Optional() private readonly assignedMethodTypes: AssignedMethodTypesState | null,
    @Optional() private readonly stagedMethodTypes: StagedMethodTypesState | null,
    @Optional() private readonly labelConfigState: LabelConfigState | null
  ) {}

  // ============================================================================
  // Session context for the colour dialog
  // ============================================================================

  /**
   * Everything the multi-assign colour dialog needs from THIS editing session,
   * as one snapshot:
   *
   * - `methodTypes` — the method types this session knows about but a backend
   *   query never could, on top of the dialog's own usage read.
   * - `contextMethodTypeId` — the method type the session is editing, when it
   *   has one. The dialog opens pre-checked and pre-coloured on it. Absent on
   *   the standalone pipeline routes, where the MethodType session state is not
   *   in scope and the pipeline step editor facade falls back to none.
   *
   * ONE selector rather than two, deliberately: combined at the call site the
   * two fields would be free to arrive from different emissions, and the id
   * could then name a method type missing from the list it shipped with.
   * Reading the id off the list's first entry is likewise wrong — the merge
   * below folds several sources and its order is incidental.
   *
   * Plain derived observable, no `effect()`: cross-state derivation belongs to
   * this tier, and the opener just `take(1)`s it at click time.
   *
   * The session's OWN method type is always part of `methodTypes` — that is the
   * row an administrator colouring a freshly added label needs, and inside a
   * case-type session the ledgers may not carry it. It is contributed whatever
   * the assignment state: this editor is editing that step's colours regardless.
   *
   * The case-type staging states are `@Optional()` because no scope is forced to
   * provide them: inside a case-type session they are the page's seeded ledgers,
   * while the standalone pipeline routes get unseeded instances off
   * `saveProtocolCascadeProviders` (empty context either way), and a scope that
   * mounts this editor without the cascade at all resolves them as null.
   */
  public readonly labelConfigDialogContext$: Observable<LabelConfigDialogContext> = this.state.select(
    this.state.methodType$,
    this.assignedMethodTypes ? this.assignedMethodTypes.combinedAssigned$ : of<MethodTypeItem[]>([]),
    this.stagedMethodTypes ? this.stagedMethodTypes.pendingMethodTypes$ : of<StagedMethodTypeDraft[]>([]),
    this.stagedMethodTypes
      ? this.stagedMethodTypes.methodTypeOverrides$
      : of<Record<string, StagedMethodTypeOverride>>({}),
    (sessionMethodType, assigned, pending, overrides) => ({
      methodTypes: this.mergeContext(sessionMethodType, assigned, pending, overrides),
      contextMethodTypeId: sessionMethodType?.id,
    })
  );

  // ============================================================================
  // Label-colour preview
  // ============================================================================

  /**
   * The colour each option label shows on the session's method type: staged
   * configs from the shared ledger (live — a colour-dialog submit updates the
   * preview reactively, and covers unsaved labels and unsaved method types)
   * merged over the colours persisted at session initialize. Same precedence
   * the colour dialog itself applies. Empty when the session has no
   * method-type context or the step does not support label colours — the
   * options list then keeps its plain palette icons.
   *
   * The shared ledger is `@Optional()` for the same reason as the case-type
   * staging states above: no scope is forced to provide it.
   */
  public readonly labelColorsByUuid$: Observable<Record<string, string>> = this.state.select(
    this.state.methodType$,
    this.state.supportsLabelColors$,
    this.state.persistedLabelColors$,
    this.labelConfigState ? this.labelConfigState.stagedConfigs$ : of<StagedLabelConfig[]>([]),
    (methodType, supportsLabelColors, persisted, staged) => {
      if (!methodType || !supportsLabelColors) return {};
      /**
       * DELIBERATE DUPLICATION: the "staged config supersedes the stored colour"
       * precedence is also applied by `mergeRows` in
       * label-config-multi-assign.effects.ts — on the transposed axis (one method
       * type across many labels here, one label across many method types there)
       * and producing a different output shape. Sharing it would mean relocating
       * `StagedLabelConfig` into @protocols/domain, which is disproportionate for a
       * two-line kernel. A CHANGE TO THE PRECEDENCE MUST BE APPLIED IN BOTH PLACES.
       */
      const colors = { ...persisted };
      for (const config of staged) {
        if (config.methodTypeId === methodType.id) colors[config.labelUuid] = config.color;
      }
      return colors;
    }
  );

  // ============================================================================
  // Dirty-tracking signature
  // ============================================================================

  /**
   * Live canonical signature of the editing buffer. Single source for both the
   * facade's `hasChanges$` compare and `captureBaseline`'s snapshot.
   *
   * Category + options are projected from a single state snapshot (combining
   * sibling selectors would glitch on the `initialize` emission).
   *
   * NOTE: declared above `captureBaseline` on purpose — its initializer reads
   * this property at construction time.
   */
  public readonly currentSignature$: Observable<string> = this.state.select((state) =>
    FormSignatureService.compute(this.projectSignature(state.category, state.options))
  );

  // ============================================================================
  // Seed
  // ============================================================================

  /**
   * Seeds a fresh editing session and baselines its signature so the facade's
   * `hasChanges$` can detect divergence. This is the one production seed path
   * (consumers must route through it, not `state.initialize`, or the session
   * stays un-baselined and reads as clean forever). Seeding is synchronous, so
   * capturing right after the updater cannot race.
   *
   * Each seed also fetches the persisted label colours of the session's
   * method type (once — persisting happens after editors close, so the slice
   * cannot go stale mid-session). `switchMap` drops a still-in-flight fetch
   * when a new session seeds, so a slow read can never land in the wrong
   * session.
   */
  public readonly initialize = this.state.effect((input$: Observable<PipelineCategoryInitialInput>) =>
    input$.pipe(
      tap((input: PipelineCategoryInitialInput) => {
        this.state.initialize(input);
        this.captureBaseline();
      }),
      switchMap((input) => this.fetchPersistedLabelColors$(input))
    )
  );

  /**
   * Resolves the persisted label colours for the seeded session, or nothing
   * when the preview cannot apply: no method-type context, or colours not
   * supported on this step. A failed read is swallowed — the preview is
   * auxiliary and must never break the editor, which simply keeps its palette
   * icons. A MISSING PROVIDER is not one of those cases: it fails at injection.
   */
  private fetchPersistedLabelColors$(input: PipelineCategoryInitialInput): Observable<unknown> {
    const methodTypeId = input.methodType?.id;
    const supportsLabelColors = input.supportsLabelColors ?? true;
    if (!methodTypeId || !supportsLabelColors) return EMPTY;

    return from(this.listLabelColorsQuery.execute({ methodTypeId })).pipe(
      tap((rows) =>
        this.state.setPersistedLabelColors(Object.fromEntries(rows.map((row) => [row.labelUuid, row.color])))
      ),
      catchError(() => EMPTY)
    );
  }

  /** Snapshots the current buffer signature into `baselineSignature`. */
  private readonly captureBaseline = this.state.effect<void>((trigger$) =>
    trigger$.pipe(
      withLatestFrom(this.currentSignature$),
      tap(([, signature]) => this.state.setBaselineSignature(signature))
    )
  );

  // ============================================================================
  // Category + option selection
  // ============================================================================

  public readonly submitFreeTextCategory = this.state.effect((name$: Observable<string>) =>
    name$.pipe(
      tap((name: string) => {
        const trimmed = name.trim();
        if (!trimmed) return;
        this.state.setCategoryFromLabel({ uuid: uuidv4(), name: { en: trimmed }, pendingPersistence: 'save' });
      })
    )
  );

  public readonly submitFreeTextOption = this.state.effect((name$: Observable<string>) =>
    name$.pipe(
      tap((name: string) => {
        const trimmed = name.trim();
        if (!trimmed) return;
        this.state.addOption({ uuid: uuidv4(), name: { en: trimmed }, thrdis: 0, pendingPersistence: 'save' });
      })
    )
  );

  public readonly removeOption = this.state.removeOption;

  // ============================================================================
  // Save / cancel
  // ============================================================================

  public readonly save = this.state.effect<void>((trigger$) =>
    trigger$.pipe(
      withLatestFrom(this.state.category$, this.state.options$, this.state.methodType$),
      tap(([, category, options, methodType]) => {
        if (!category) {
          this.state.setError('pipeline_category_editor.save_label_error');
          return;
        }
        const result: PipelineCategoryResult = {
          category,
          options,
          methodTypeId: methodType?.id,
        };
        this.state.setResult(result);
        this.state.setStatus('saved');
      })
    )
  );

  public readonly cancel = this.state.effect<void>((trigger$) =>
    trigger$.pipe(
      withLatestFrom(this.state.options$, this.state.category$),
      tap(([, options, category]) => {
        const cleared = options.map((option) => ({ ...option, pendingPersistence: undefined }));
        this.state.replaceOptions(cleared);
        if (category && category.pendingPersistence !== undefined) {
          this.state.setCategoryFromLabel({
            uuid: category.uuid,
            name: category.name,
            pendingPersistence: undefined,
          });
        }
        this.state.setStatus('cancelled');
      })
    )
  );

  // ============================================================================
  // Signature projection
  // ============================================================================

  /**
   * Fingerprint of the category-editor buffer: everything a cancel would
   * discard, and nothing else. Serialized by `FormSignatureService.compute`
   * (recursive key sort), so key-insertion order never flips the signature.
   *
   * Included — category identity (`uuid`) + name, and the options in ARRAY
   * ORDER (reordering is real work, see the facade's `reorderOptions`) with
   * their `uuid`, name and `thrdis` (thresholds are written back onto the
   * option by the facade's `updateOptionThreshold`, driven by the threshold
   * dialog).
   *
   * Excluded on purpose:
   * - `pendingPersistence` — persistence bookkeeping, not content; every user
   *   action that flips it also perturbs an included field.
   * - `categoryInput` / `optionInput` — transient keystrokes; committing them
   *   lands in `category`/`options`, which IS captured.
   * - `methodType` / `variant` / `readonly` — session context set by the
   *   consumer, never user work.
   * - staged label colors — they live in the shared `LabelConfigState` and the
   *   `cancel` effect does NOT unstage them, so a color-only edit survives
   *   cancel (persisted by the save cascade) and must not trip the warning.
   * - loading/saving/error/status/result/editOption slices/sessionToken — plumbing.
   */
  private projectSignature(
    category: { uuid: string; name: LocalizedName; pendingPersistence: PendingPersistence } | null,
    options: PipelineCategoryOption[]
  ): unknown {
    return {
      category: category ? { uuid: category.uuid, name: this.normalizeLangMap(category.name) } : null,
      options: options.map((option) => ({
        uuid: option.uuid,
        name: this.normalizeLangMap(option.name),
        thrdis: option.thrdis,
      })),
    };
  }

  // ============================================================================
  // Session-context merge
  // ============================================================================

  /**
   * Folds the session's method-type sources into one flat list, keyed by id, in
   * precedence order: the session's own method type, then the case type's
   * assigned set, then the drafts staged this session. Later sources win on
   * `name` — a rename staged this session is newer than whatever the earlier
   * source was seeded with — while a source that carries no `MethodTypeKind`
   * leaves the kind an earlier one supplied intact (and the icon with it).
   *
   * The session's own method type is always contributed, whatever the case type
   * assigns: this editor is editing that step's colours regardless. It is not
   * always there to contribute, though — the MethodType session state is only
   * provided by the method-type editor scope, so the standalone pipeline routes
   * have none and the pipeline step editor facade falls back to none.
   *
   * Every override is folded in, INCLUDING those for method types the session
   * unassigned or never assigned: the association can still exist in the
   * database, and the usage query would then render the stale backend name.
   *
   * Pending drafts, by contrast, only ever REFRESH a row an earlier source
   * already produced, and mark it as client-only. A draft created and then
   * dropped from the case type stays in that ledger for the rest of the session
   * (`unassign` never unstages it), and offering it would let the user colour a
   * method type the save cascade prunes — the row would vanish silently at save.
   */
  private mergeContext(
    sessionMethodType: MethodTypeSelectorItem | null,
    assigned: MethodTypeItem[],
    pending: StagedMethodTypeDraft[],
    overrides: Record<string, StagedMethodTypeOverride>
  ): LabelConfigContextMethodType[] {
    const byId = new Map<string, LabelConfigContextMethodType>();
    if (sessionMethodType) {
      byId.set(sessionMethodType.id, {
        id: sessionMethodType.id,
        name: sessionMethodType.name,
        type: sessionMethodType.type,
      });
    }
    for (const methodType of assigned) {
      const existing = byId.get(methodType.id);
      byId.set(methodType.id, {
        ...existing,
        id: methodType.id,
        name: methodType.name,
        type: methodType.type ?? existing?.type,
        pending: methodType.pending,
      });
    }
    for (const draft of pending.filter((candidate) => byId.has(candidate.id))) {
      byId.set(draft.id, { ...byId.get(draft.id), id: draft.id, name: draft.name, pending: true });
    }
    for (const override of Object.values(overrides)) {
      byId.set(override.draft.id, { ...byId.get(override.draft.id), id: override.draft.id, name: override.draft.name });
    }
    return Array.from(byId.values());
  }

  /** Drops empty entries so `{}` ≡ `{en: ''}`; key sorting is the service's job. */
  private normalizeLangMap(map: LocalizedName): { [lang: string]: string } {
    return Object.fromEntries(
      Object.entries(map).filter(([, value]) => value !== undefined && value !== null && value !== '')
    );
  }
}
